import axios from 'axios';
import { refreshToken } from './auth';


// API Base URL: from env at build time (REACT_APP_API_BASE_URL), else default production
// Build for live:  npm run build   (or set in .env.production)
// Build for test:  REACT_APP_API_BASE_URL=https://test.api.checkstall.com/api/v2 npm run build
//export const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://192.168.0.101:8000/api/v2';
//export const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'https://api.checkstall.com/api/v2';
export const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'https://test.api.checkstall.com/api/v2';

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add auth token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle auth errors and token refresh
api.interceptors.response.use(
  (response) => {
    return response;
  },
  async (error) => {
    const originalRequest = error.config;

    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      try {
        // Try to refresh the token
        const newToken = await refreshToken();
        
        // Update the original request with the new token
        originalRequest.headers.Authorization = `Bearer ${newToken}`;
        
        // Retry the original request
        return api(originalRequest);
      } catch (refreshError) {
        // If refresh fails, clear tokens and redirect to login
        console.log('Token refresh failed, clearing local storage...');
        localStorage.removeItem('token');
        localStorage.removeItem('refresh_token');
        localStorage.removeItem('loginLockout');
        
        // Dispatch a custom event to trigger logout in the app
        window.dispatchEvent(new CustomEvent('auth:logout'));
        return Promise.reject(refreshError);
      }
    }

    return Promise.reject(error);
  }
);

// Country API
export const getCountries = async (params = {}) => {
  const res = await api.get('/admin/countries/', { params });
  return res.data;
};
export const createCountry = async (data) => {
  const res = await api.post('/admin/countries/', data);
  return res.data;
};
export const updateCountry = async (id, data) => {
  const res = await api.put(`/admin/countries/${id}/`, data);
  return res.data;
};
export const deleteCountry = async (id) => {
  const res = await api.delete(`/admin/countries/${id}/`);
  return res.data;
};

// State API
export const getStates = async (params = {}) => {
  const res = await api.get('/admin/states/', { params });
  return res.data;
};
export const createState = async (data) => {
  const res = await api.post('/admin/states/', data);
  return res.data;
};
export const updateState = async (id, data) => {
  const res = await api.put(`/admin/states/${id}/`, data);
  return res.data;
};
export const deleteState = async (id) => {
  const res = await api.delete(`/admin/states/${id}/`);
  return res.data;
};

// City API
export const getCities = async (params = {}) => {
  const res = await api.get('/admin/cities/', { params });
  return res.data;
};
export const createCity = async (data) => {
  const res = await api.post('/admin/cities/', data);
  return res.data;
};
export const updateCity = async (id, data) => {
  const res = await api.put(`/admin/cities/${id}/`, data);
  return res.data;
};
export const deleteCity = async (id) => {
  const res = await api.delete(`/admin/cities/${id}/`);
  return res.data;
};

// Bank API
export const getBanks = async (params = {}) => {
  const res = await api.get('/banks/', { params });
  return res.data;
};
export const createBank = async (data) => {
  const res = await api.post('/banks/', data);
  return res.data;
};
export const updateBank = async (id, data) => {
  const res = await api.put(`/banks/${id}/`, data);
  return res.data;
};
export const deleteBank = async (id) => {
  const res = await api.delete(`/banks/${id}/`);
  return res.data;
};

// Order API
export const getOrders = async (params = {}) => {
  const res = await api.get('/admin/orders/', { params });
  return res.data;
};

export const getOrderById = async (id) => {
  const res = await api.get(`/admin/orders/${id}/`);
  return res.data;
};

export const updateOrderDetails = async (orderId, data) => {
  const res = await api.put(`/admin/orders/${orderId}/update/`, data);
  return res.data;
};

export const updateSkuDetails = async (orderId, skuId, data) => {
  const res = await api.put(`/admin/orders/${orderId}/sku/${skuId}/`, data);
  return res.data;
};

export const uploadWarehousePhotos = async (orderId, formData) => {
  const res = await api.post(`/admin/orders/${orderId}/upload-warehouse-photos/`, formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });
  return res.data;
};

export const deleteWarehousePhoto = async (orderId, photoId) => {
  const res = await api.delete(`/admin/orders/${orderId}/warehouse-photos/${photoId}/`);
  return res.data;
};

// File upload
export const uploadFile = async (formData) => {
  const res = await api.post('/admin/upload/', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });
  return res.data;
};

// Invoice API functions
export const fetchInvoices = async (params = {}) => {
  const queryParams = new URLSearchParams();
  
  // Add pagination params
  if (params.page) queryParams.append('page', params.page);
  if (params.page_size) queryParams.append('page_size', params.page_size);
  
  // Add filter params
  if (params.invoice_number) queryParams.append('invoice_number', params.invoice_number);
  if (params.user) queryParams.append('user', params.user);
  if (params.subject) queryParams.append('subject', params.subject);
  if (params.status) queryParams.append('status', params.status);
  
  const url = `/admin/orders/invoices/${queryParams.toString() ? '?' + queryParams.toString() : ''}`;
  const res = await api.get(url);
  return res.data;
};

export const createInvoice = async (invoiceData) => {
  const res = await api.post('/admin/orders/invoices/', invoiceData);
  return res.data;
};

export const updateInvoice = async (invoiceId, invoiceData) => {
  const res = await api.put(`/admin/orders/invoices/${invoiceId}/`, invoiceData);
  return res.data;
};

export const updateInvoicePayment = async (invoiceId, paymentData) => {
  const res = await api.put(`/admin/orders/invoices/${invoiceId}/payment/`, paymentData);
  return res.data;
};

export const clearInvoice = async (invoiceId) => {
  const res = await api.delete(`/admin/orders/invoices/${invoiceId}/clear/`);
  return res.data;
};

export const getUserInvoices = async () => {
  const res = await api.get('/app/orders/invoices/');
  return res.data;
};

export const fetchWithdrawalRequests = async (params = {}) => {
  const response = await api.get('/admin/wallet/withdrawal-requests/', { params });
  return response.data;
};

export const updateWithdrawalStatus = async (id, payload) => {
  const response = await api.patch(`/admin/wallet/withdrawal-requests/${id}/`, payload);
  return response.data;
};

export const getUserInvoiceDetail = async (invoiceId) => {
  const res = await api.get(`/app/orders/invoices/${invoiceId}/`);
  return res.data;
};

export const getUserInvoiceCount = async () => {
  const res = await api.get('/app/orders/invoices/count/');
  return res.data;
};

// Generic authenticated request function
export const authenticatedRequest = async (url, options = {}) => {
  try {
    const response = await api({
      url,
      method: options.method || 'GET',
      headers: {
        'Content-Type': 'application/json',
        ...options.headers,
      },
      data: options.body,
      ...options,
    });
    return response;
  } catch (error) {
    throw error;
  }
};

// RMB Exchange Rate API
export const getRMBExchangeRates = async (params = {}) => {
  const res = await api.get('/admin/rmb/exchange-rates/', { params });
  return res.data;
};

export const createRMBExchangeRate = async (data) => {
  const res = await api.post('/admin/rmb/exchange-rates/', data);
  return res.data;
};

export const updateRMBExchangeRate = async (id, data) => {
  const res = await api.put(`/admin/rmb/exchange-rates/${id}/`, data);
  return res.data;
};

export const deleteRMBExchangeRate = async (id) => {
  const res = await api.delete(`/admin/rmb/exchange-rates/${id}/`);
  return res.data;
};

// RMB Exchange Rate History API
export const getRMBExchangeRateHistory = async (params = {}) => {
  const res = await api.get('/admin/rmb/exchange-rates-history/', { params });
  return res.data;
};

// Buy RMB Transactions API
export const getBuyRMBTransactions = async (params = {}) => {
  const res = await api.get('/admin/rmb/transactions/', { params });
  return res.data;
};

export const updateBuyRMBTransaction = async (id, data) => {
  // Check if data contains a file (payment_receipt)
  const formData = new FormData();
  let hasFile = false;
  
  // If payment_receipt is a File object, use FormData
  if (data.payment_receipt instanceof File) {
    hasFile = true;
    formData.append('payment_receipt', data.payment_receipt);
  }
  
  // Append other fields
  if (data.status) formData.append('status', data.status);
  if (data.notes !== undefined) formData.append('notes', data.notes);
  
  // Use FormData if file is present, otherwise use regular JSON
  if (hasFile) {
    const res = await api.put(`/admin/rmb/transactions/${id}/`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return res.data;
  } else {
    const res = await api.put(`/admin/rmb/transactions/${id}/`, data);
    return res.data;
  }
};

// Profit Analytics API
export const getProfitAnalyticsSummary = async (params = {}) => {
  const res = await api.get('/admin/orders/profit-analytics/summary/', { params });
  return res.data;
};

export const getProfitAnalyticsOrders = async (params = {}) => {
  const res = await api.get('/admin/orders/profit-analytics/orders/', { params });
  return res.data;
};

// Wema Virtual Accounts (Checkstall Pay) – admin VA management
export const getVirtualAccountStats = async () => {
  const res = await api.get('/admin/checkstall-pay/virtual-accounts/stats/');
  return res.data;
};

export const createVirtualAccounts = async (data = {}) => {
  const res = await api.post('/admin/checkstall-pay/virtual-accounts/create/', data);
  return res.data;
};

/** Query Wema NIP transaction (TransQuery) for dispute resolution. Pass sessionid and/or craccount. */
export const queryWemaTransaction = async (body = {}) => {
  const res = await api.post('/admin/checkstall-pay/virtual-accounts/query-trans/', body);
  return res.data;
};

export const getInwardTransactions = async (params = {}) => {
  const res = await api.get('/admin/checkstall-pay/inward-transactions/', { params });
  return res.data;
};

export const getProfitAnalyticsCharts = async (params = {}) => {
  const res = await api.get('/admin/orders/profit-analytics/charts/', { params });
  return res.data;
};

// RMB Profit Analytics API
export const getRMBProfitAnalyticsSummary = async (params = {}) => {
  const res = await api.get('/admin/rmb/profit-analytics/summary/', { params });
  return res.data;
};

export const getRMBProfitAnalyticsTransactions = async (params = {}) => {
  const res = await api.get('/admin/rmb/profit-analytics/transactions/', { params });
  return res.data;
};

export const getRMBProfitAnalyticsCharts = async (params = {}) => {
  const res = await api.get('/admin/rmb/profit-analytics/charts/', { params });
  return res.data;
};

export const getSupportReviews = async (params = {}) => {
  const res = await api.get('/admin/reviews/', { params });
  return res.data;
};

export const getPendingSupportReviewsCount = async () => {
  const data = await getSupportReviews({ status: 'pending', page: 1, page_size: 1 });
  if (typeof data?.count === 'number') return data.count;
  if (Array.isArray(data?.results)) return data.results.length;
  return Array.isArray(data) ? data.length : 0;
};

export const updateSupportReview = async (id, data = {}) => {
  const res = await api.patch(`/admin/reviews/${id}/`, data);
  return res.data;
};

export const approveSupportReview = async (id) => {
  const res = await api.post(`/admin/reviews/${id}/approve/`, {});
  return res.data;
};

export const rejectSupportReview = async (id) => {
  const res = await api.post(`/admin/reviews/${id}/reject/`, {});
  return res.data;
};

export const featureSupportReview = async (id, isFeatured) => {
  const payload = isFeatured === undefined ? {} : { is_featured: isFeatured };
  const res = await api.post(`/admin/reviews/${id}/feature/`, payload);
  return res.data;
};

export default api; 
